import localizeCurrency from "@helpers/localizeCurrency"
import { getPlanDetails } from "@helpers/plans"
import resolvePrice from "@helpers/pricing"
import { PlanPrices } from "@loaders/getPricesLoader"
import { useTranslation } from "react-i18next"
import { Plan } from "src/types"
import i18n from "../../i18n"

interface Props {
  animationDelayMs: number
  currency: string
  isPopular: boolean
  isSelected: boolean
  onSelect: () => void
  plan: Plan
  prices: PlanPrices
}

function splitPerDay(amountInCents: number, currency: string) {
  const parts = new Intl.NumberFormat(i18n.language, {
    currency,
    minimumFractionDigits: 2,
    style: "currency",
  }).formatToParts(amountInCents / 100)

  const symbol = parts.find((part) => part.type === "currency")?.value ?? ""
  const amount = parts
    .filter((part) =>
      ["decimal", "fraction", "group", "integer"].includes(part.type),
    )
    .map((part) => part.value)
    .join("")

  return { amount, symbol }
}

export default function PaywallPlanCard({
  animationDelayMs,
  currency,
  isPopular,
  isSelected,
  onSelect,
  plan,
  prices,
}: Props) {
  const { t } = useTranslation()

  const introPrice = resolvePrice({ currency, stripePrice: prices.intro })
  const fullPrice = resolvePrice({ currency, stripePrice: prices.full })

  if (!introPrice || !fullPrice) return null

  const { days } = getPlanDetails(plan)
  const perDay = splitPerDay(
    introPrice.amountInCents / days,
    introPrice.currency,
  )
  const hasDiscount = introPrice.amountInCents < fullPrice.amountInCents
  const savePercent = Math.round(
    (1 - introPrice.amountInCents / fullPrice.amountInCents) * 100,
  )

  return (
    <button
      aria-pressed={isSelected}
      className={`relative w-full animate-fade-up overflow-hidden rounded-vm-md border-2 text-left transition-[border-color,background-color,transform] duration-200 active:scale-[0.99] ${
        isSelected
          ? "border-vm-primary bg-vm-primary-soft"
          : "border-vm-line bg-vm-surface"
      }`}
      onClick={onSelect}
      style={{ animationDelay: `${animationDelayMs}ms` }}
      type="button"
    >
      {isPopular && (
        <div className="relative overflow-hidden bg-vm-primary-solid py-1 text-center text-[11px] font-bold uppercase tracking-wider text-white">
          <span className="absolute inset-0 animate-shine bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          <span className="relative">
            {t("pages.checkout.paywall.recommended")}
          </span>
        </div>
      )}

      <div className="flex items-center gap-2.5 px-3 py-3.5">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
            isSelected ? "border-vm-primary" : "border-vm-muted/50"
          }`}
        >
          {isSelected && (
            <span className="h-2.5 w-2.5 animate-check-pop rounded-full bg-vm-primary" />
          )}
        </span>

        <div className="min-w-0 grow">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span className="whitespace-nowrap text-base font-bold text-vm-ink">
              {t(`pages.checkout.paywall.planLabels.${plan}`)}
            </span>
            {hasDiscount && savePercent > 0 && (
              <span
                className={`whitespace-nowrap rounded px-1 py-0.5 text-[9px] font-bold uppercase transition-colors ${
                  isSelected
                    ? "bg-vm-primary-solid text-white"
                    : "bg-vm-primary-soft text-vm-primary-text"
                }`}
              >
                {t("pages.checkout.paywall.save", { percent: savePercent })}
              </span>
            )}
          </p>
          {/* NOTE: Prices aren't translated (and translation rewriting this
          text next to the conditional strike-through used to crash React). */}
          <p className="mt-0.5 text-xs text-vm-muted" translate="no">
            {hasDiscount && (
              <>
                <span className="line-through">
                  {localizeCurrency(fullPrice)}
                </span>
                {" → "}
              </>
            )}
            {localizeCurrency(introPrice)}
          </p>
        </div>

        <div
          className={`flex shrink-0 flex-col items-end transition-colors ${
            isSelected ? "text-vm-ink" : "text-vm-muted/70"
          }`}
        >
          <span className="flex items-start leading-none" translate="no">
            <span className="mr-0.5 mt-0.5 text-sm font-semibold">
              {perDay.symbol}
            </span>
            <span className="text-[26px] font-extrabold tracking-tight">
              {perDay.amount}
            </span>
          </span>
          <span className="text-[11px] font-medium text-vm-muted">
            {t("pages.checkout.plans.perDay")}
          </span>
        </div>
      </div>
    </button>
  )
}
